/**
 * The menu shared with WeHunt Web. Both apps render the same items in the
 * same order and hop to each other for the items they do not own; WeHunt Web
 * keeps its own copy of this list, so a change here is a change there too.
 * The contract is the menu table in wehunt-platform's
 * docs/superpowers/specs/2026-09-10-cross-app-menu-design.md.
 *
 * This module is the single source of the account menu: the my-account
 * sidebar, its mobile bottom bar and the header menu's Account section all
 * render ACCOUNT_MENU_ITEMS, so an item can never be enabled in one place and
 * missing from another.
 */

import type { NavIconName } from "@/components/icons/navIcons";
import { withThemeParam, type Theme } from "@/utils/theme";

/** Reserved prefix for the stable entry routes other apps link to. */
const GO_PREFIX = "/go/";

export function isGoPath(pathname: string): boolean {
  return pathname.startsWith(GO_PREFIX);
}

export interface AccountMenuItem {
  key: string;
  /** CMS key already in use by the my-account sidebar, reused verbatim. */
  labelKey: string;
  /** English copy shown when the dictionary has no entry. */
  fallback: string;
  icon: NavIconName;
  /**
   * A disabled item is still listed — the design shows the full menu in both
   * apps — but renders as an inert "coming soon" row rather than a link.
   */
  enabled: boolean;
}

/**
 * Every account item in the agreed order. "warranties" is listed but disabled:
 * MyAccountPage has no branch for it yet, and the shared menu shows it greyed
 * out (in this shop and in WeHunt Web alike) until it ships.
 */
export const ACCOUNT_MENU_ITEMS = [
  {
    key: "dashboard",
    labelKey: "Tracker.MyAccount.Nav.Dashboard",
    fallback: "Dashboard",
    icon: "dashboard",
    enabled: true,
  },
  {
    key: "products",
    labelKey: "Tracker.MyAccount.Nav.Products",
    fallback: "Products",
    icon: "products",
    enabled: true,
  },
  {
    key: "subscriptions",
    labelKey: "Tracker.MyAccount.Nav.Subscriptions",
    fallback: "Subscriptions",
    icon: "subscriptions",
    enabled: true,
  },
  {
    key: "orders",
    labelKey: "Tracker.MyAccount.Nav.Orders",
    fallback: "Orders",
    icon: "orders",
    enabled: true,
  },
  {
    key: "address",
    labelKey: "Tracker.MyAccount.Nav.AddressBook",
    fallback: "Address book",
    icon: "address",
    enabled: true,
  },
  {
    key: "warranties",
    labelKey: "Tracker.MyAccount.Nav.Warranties",
    fallback: "Warranties",
    icon: "warranties",
    enabled: false,
  },
  {
    key: "wallet",
    labelKey: "Tracker.MyAccount.Nav.Wallet",
    fallback: "Wallet",
    icon: "wallet",
    enabled: true,
  },
] as const satisfies readonly AccountMenuItem[];

type EnabledAccountMenuItem = Extract<
  (typeof ACCOUNT_MENU_ITEMS)[number],
  { enabled: true }
>;

/** The keys the account page can actually open. */
export type AccountTab = EnabledAccountMenuItem["key"];

/** Every key in the menu, enabled or not. */
export type AccountMenuKey = (typeof ACCOUNT_MENU_ITEMS)[number]["key"];

export const ACCOUNT_TABS: readonly AccountTab[] = ACCOUNT_MENU_ITEMS.filter(
  (item): item is EnabledAccountMenuItem => item.enabled
).map((item) => item.key);

const DEFAULT_ACCOUNT_TAB: AccountTab = "dashboard";

/**
 * The B2B account page has its own, smaller tab set; of the shared tabs it
 * knows only these. Anything else lands a B2B member on their dashboard rather
 * than on an empty tab. Centralised here so the card and the /go/account route
 * narrow the same way instead of each hardcoding its own B2B tab list.
 */
const B2B_ACCOUNT_TABS: readonly AccountTab[] = ["dashboard", "orders"];

export function accountTabsFor(isB2b: boolean): readonly AccountTab[] {
  return isB2b ? B2B_ACCOUNT_TABS : ACCOUNT_TABS;
}

/**
 * The account rows a menu should list for this member: B2B members see only
 * the tabs their portal has (all enabled); everyone else sees the full list,
 * disabled rows included.
 */
export function accountMenuItemsFor(
  isB2b: boolean
): readonly AccountMenuItem[] {
  if (!isB2b) return ACCOUNT_MENU_ITEMS;
  const allowed = new Set<string>(B2B_ACCOUNT_TABS);
  return ACCOUNT_MENU_ITEMS.filter((item) => allowed.has(item.key));
}

export function isAccountTab(raw: string | undefined): raw is AccountTab {
  return ACCOUNT_TABS.some((candidate) => candidate === raw);
}

export function toAccountTab(
  raw: string | undefined,
  isB2b: boolean
): AccountTab {
  if (!isAccountTab(raw)) return DEFAULT_ACCOUNT_TAB;
  if (isB2b && !accountTabsFor(true).includes(raw)) return DEFAULT_ACCOUNT_TAB;
  return raw;
}

export function goAccountPath(tab: AccountTab): string {
  return `${GO_PREFIX}account/${tab}`;
}

/**
 * Whether `location` is the account page showing `tab`. The account page
 * addresses its tabs with `?tab=`, and no `tab` parameter means the dashboard.
 * A trailing slash on either side is ignored — the CMS gives the page URL
 * with one, the router reports it without.
 */
export function isCurrentAccountTab(
  tab: AccountTab,
  accountPageHref: string,
  location: { pathname: string; search: string }
): boolean {
  const accountPath = new URL(accountPageHref, "http://placeholder").pathname;
  if (trimSlash(location.pathname) !== trimSlash(accountPath)) return false;
  const current = new URLSearchParams(location.search).get("tab");
  return (current ?? DEFAULT_ACCOUNT_TAB) === tab;
}

function trimSlash(path: string): string {
  return path.replace(/\/+$/, "");
}

/** Items that live in WeHunt Web. New CMS keys; the fallbacks are the English copy. */
export const WEB_APP_ITEMS = [
  {
    id: "overview",
    path: "/",
    key: "Tracker.WebApp.Nav.Overview",
    fallback: "Overview",
    icon: "overview",
  },
  {
    id: "hunts",
    path: "/hunts",
    key: "Tracker.WebApp.Nav.Hunts",
    fallback: "Hunts",
    icon: "hunts",
  },
  {
    id: "teams",
    path: "/teams",
    key: "Tracker.WebApp.Nav.Teams",
    fallback: "Teams & grounds",
    icon: "teams",
  },
] as const satisfies readonly {
  id: string;
  path: string;
  key: string;
  fallback: string;
  icon: NavIconName;
}[];

/** Settings is WeHunt Web's; the shop's own settings page is disabled. */
export const SETTINGS_ITEM = {
  path: "/settings",
  key: "Tracker.MyAccount.Nav.Settings",
  fallback: "Settings",
  icon: "settings",
} as const satisfies {
  path: string;
  key: string;
  fallback: string;
  icon: NavIconName;
};

/** The sign-out row that closes the account menu in both apps. */
export const SIGN_OUT_ITEM = {
  key: "Tracker.MyAccount.SignOut",
  fallback: "Sign out",
  icon: "signout",
} as const satisfies { key: string; fallback: string; icon: NavIconName };

/** The dark-mode switch row that sits with Sign out in both apps. */
export const THEME_TOGGLE_LABELS = {
  dark: { key: "Tracker.MyAccount.DarkMode", fallback: "Dark mode" },
  light: { key: "Tracker.MyAccount.LightMode", fallback: "Light mode" },
} as const;

/** Tooltip on a disabled row. */
export const COMING_SOON_LABEL = {
  key: "Tracker.MyAccount.ComingSoon",
  fallback: "Coming soon",
} as const;

/** Section headings. New CMS keys. */
export const SECTION_LABELS = {
  webApp: { key: "Tracker.Menu.WebApp", fallback: "Web app" },
  account: { key: "Tracker.Menu.Account", fallback: "Account" },
} as const;

/**
 * A link into WeHunt Web. With a theme it carries the member's light/dark
 * choice across the domain boundary as `?theme=`, which the web app adopts on
 * arrival (and hands back the same way — see consumeThemeFromUrl).
 */
export function webAppHref(
  webAppUrl: string,
  path: string,
  theme?: Theme
): string {
  const href = `${webAppUrl.replace(/\/+$/, "")}${path}`;
  return theme ? withThemeParam(href, theme) : href;
}
